import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ExternalLink, FileText, X } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ComplianceDocument } from "@osmix/contracts/compliance";
import { api } from "@/api";
import { productKeys } from "@/api/hooks/use-products";
import { complianceSummaryKey } from "@/components/compliance-strip";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";

/**
 * Проверка документа о соответствии модератором (31.10.2026): файл, номер и
 * его формат, ссылка на реестр, срок действия, «Подтвердить» / «Отклонить» с
 * причиной. Одна панель - в очереди документов и в карточке товара.
 */

export const complianceQueueKey = ["compliance-queue"] as const;
export const complianceJournalKey = ["compliance-journal"] as const;
export const ruDay = (value: string | null | undefined) => {
  if (!value) return "";
  const [y, m, d] = value.slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
};

/** Открыть приватный файл документа в новой вкладке (запрос с токеном → Blob). */
export const openComplianceFile = async (id: string) => {
  const tab = window.open("", "_blank");
  try {
    const blob = await api.compliance.documentFile(id);
    const url = URL.createObjectURL(blob);
    if (tab) tab.location.href = url;else window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    tab?.close();
    throw e;
  }
};

/** Статус документа бейджем. */
export function DocumentStatusBadge(t0) {
  const $ = _c(16);
  const {
    doc
  } = t0;
  const {
    t
  } = useTranslation();
  if (doc.status === "rejected") {
    let t1;
    if ($[0] !== t) {
      t1 = t("compliance.review.status.rejected");
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <Badge variant="destructive">{t1}</Badge>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (doc.expired) {
    let t1;
    if ($[4] !== t) {
      t1 = t("compliance.review.status.expired");
      $[4] = t;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== t1) {
      t2 = <Badge variant="destructive">{t1}</Badge>;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  if (doc.status === "approved") {
    let t1;
    if ($[8] !== t) {
      t1 = t("compliance.review.status.approved");
      $[8] = t;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    let t2;
    if ($[10] !== t1) {
      t2 = <Badge>{t1}</Badge>;
      $[10] = t1;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    return t2;
  }
  let t1;
  if ($[12] !== t) {
    t1 = t("compliance.review.status.pending");
    $[12] = t;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  let t2;
  if ($[14] !== t1) {
    t2 = <Badge variant="outline" className="border-amber-400 text-amber-800 dark:text-amber-300">{t1}</Badge>;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
}

/** Формат номера: похож на номер реестра или нет. */
export function NumberCheck(t0) {
  const $ = _c(4);
  const {
    doc
  } = t0;
  const {
    t
  } = useTranslation();
  const check = doc.numberCheck;
  if (!check) {
    return null;
  }
  let t1;
  if ($[0] !== check.format || $[1] !== check.ok || $[2] !== t) {
    t1 = check.ok ? <span className="text-xs text-emerald-700" data-testid="compliance-number-ok">{check.format ? t(`compliance.review.format.${check.format}`) : t("compliance.review.formatOk")}</span> : <span className="text-xs text-destructive" data-testid="compliance-number-bad">{check.format ? t("compliance.review.formatOtherKind", {
        format: t(`compliance.review.format.${check.format}`)
      }) : t("compliance.review.formatBad")}</span>;
    $[0] = check.format;
    $[1] = check.ok;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
export function ComplianceReviewPanel({
  doc,
  onDone
}: {
  doc: ComplianceDocument;
  onDone?: (doc: ComplianceDocument) => void;
}) {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuth(s => s.user);
  const canModerate = hasAdminPermission(user, "action.products.moderate");
  const [validUntil, setValidUntil] = useState(doc.validUntil ?? "");
  const [unlimited, setUnlimited] = useState(!doc.validUntil);
  const [comment, setComment] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const products = useQuery({
    queryKey: ["compliance-document-products", doc.id],
    queryFn: () => api.compliance.documentProducts(doc.id)
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: сброс формы при смене документа.
  useEffect(() => {
    setValidUntil(doc.validUntil ?? "");
    setUnlimited(!doc.validUntil);
    setRejecting(false);
    setReason("");
    setComment("");
    setError(null);
  }, [doc.id, doc.validUntil]);
  const refresh = () => Promise.all([queryClient.invalidateQueries({
    queryKey: complianceQueueKey
  }), queryClient.invalidateQueries({
    queryKey: complianceJournalKey
  }), queryClient.invalidateQueries({
    queryKey: complianceSummaryKey
  }), queryClient.invalidateQueries({
    queryKey: productKeys.all
  }), queryClient.invalidateQueries({
    queryKey: ["compliance-document-products", doc.id]
  }), queryClient.invalidateQueries({
    queryKey: ["compliance-report"]
  })]);
  const act = async (run: () => Promise<ComplianceDocument>) => {
    setBusy(true);
    setError(null);
    try {
      const next = await run();
      await refresh();
      onDone?.(next);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const approve = () => act(() => api.compliance.approveDocument(doc.id, {
    validUntil: unlimited ? null : validUntil || null,
    ...(comment.trim() ? {
      comment: comment.trim()
    } : {})
  }));
  const reject = () => {
    if (!reason.trim()) {
      setError(t("compliance.detail.rejectRequired"));
      return;
    }
    void act(() => api.compliance.rejectDocument(doc.id, reason.trim()));
  };
  const linked = products.data ?? [];
  const published = linked.filter(p => p.status === "published").length;
  return <div className="space-y-3 text-sm" data-testid="compliance-review-panel">
      <div className="flex flex-wrap items-center gap-2">
        <DocumentStatusBadge doc={doc} />
        <span className="text-muted-foreground">{t(`compliance.kinds.${doc.kind}`)}</span>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
        <dt className="text-muted-foreground">{t("compliance.detail.number")}</dt>
        <dd className="min-w-0">
          <span className="block break-all font-medium" data-testid="compliance-review-number">
            {doc.number}
          </span>
          <NumberCheck doc={doc} />
        </dd>
        {doc.shopName !== undefined && <>
            <dt className="text-muted-foreground">{t("compliance.review.shop")}</dt>
            <dd className="min-w-0 break-words">{doc.shopName || doc.shopId}</dd>
          </>}
        <dt className="text-muted-foreground">{t("compliance.detail.productName")}</dt>
        <dd className="min-w-0 break-words">{doc.productName}</dd>
        {doc.brand && <>
            <dt className="text-muted-foreground">{t("compliance.detail.brand")}</dt>
            <dd className="min-w-0 break-words">{doc.brand}</dd>
          </>}
        {doc.issuedAt && <>
            <dt className="text-muted-foreground">{t("compliance.detail.issuedAt")}</dt>
            <dd>{ruDay(doc.issuedAt)}</dd>
          </>}
        <dt className="text-muted-foreground">{t("compliance.detail.validUntil")}</dt>
        <dd className={doc.expired ? "text-destructive" : undefined}>
          {doc.validUntil ? ruDay(doc.validUntil) : t("compliance.detail.unlimited")}
        </dd>
        <dt className="text-muted-foreground">{t("compliance.review.uploaded")}</dt>
        <dd>{ruDay(doc.createdAt)}</dd>
        {doc.status === "rejected" && doc.rejectReason && <>
            <dt className="text-muted-foreground">{t("compliance.detail.rejectReason")}</dt>
            <dd className="text-destructive">{doc.rejectReason}</dd>
          </>}
        {doc.status === "approved" && doc.reviewComment && <>
            <dt className="text-muted-foreground">{t("compliance.review.comment")}</dt>
            <dd>{doc.reviewComment}</dd>
          </>}
      </dl>
      <p className="text-xs text-muted-foreground">
        {t("compliance.review.linked", {
        count: linked.length,
        published
      })}
      </p>
      <div className="flex flex-wrap gap-2">
        {doc.file && <Button size="sm" variant="outline" onClick={() => void openComplianceFile(doc.id).catch(e_0 => setError((e_0 as Error).message))}>
            <FileText className="size-4" /> {t("compliance.detail.openFile")}
          </Button>}
        {doc.checkUrl && <Button size="sm" variant="outline" asChild>
            <a href={doc.checkUrl} target="_blank" rel="noreferrer" title={doc.registryUrl ? undefined : t("compliance.detail.registryHint")}>
              <ExternalLink className="size-4" /> {t("compliance.detail.registry")}
            </a>
          </Button>}
        <Button size="sm" variant="ghost" onClick={() => void navigator.clipboard?.writeText(doc.number)}>
          {t("compliance.review.copy")}
        </Button>
      </div>
      {canModerate && !rejecting && <div className="space-y-2 rounded-md border p-3">
          <p className="font-medium">{t("compliance.review.decision")}</p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1 text-xs">
              <span className="text-muted-foreground">{t("compliance.review.validUntil")}</span>
              <Input type="date" value={unlimited ? "" : validUntil} disabled={unlimited} onChange={e_1 => setValidUntil(e_1.target.value)} className="w-44" data-testid="compliance-review-valid-until" />
            </div>
            <label className="flex items-center gap-2 pb-2 text-xs">
              <input type="checkbox" checked={unlimited} onChange={e_2 => setUnlimited(e_2.target.checked)} />
              {t("compliance.detail.unlimited")}
            </label>
          </div>
          <Input value={comment} onChange={e_3 => setComment(e_3.target.value)} placeholder={t("compliance.review.commentPlaceholder")} maxLength={1000} />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => void approve()} disabled={busy || doc.status === "approved"} data-testid="compliance-approve">
              <Check className="size-4" /> {t("compliance.review.approve")}
            </Button>
            <Button size="sm" variant="outline" className="text-destructive" onClick={() => setRejecting(true)} disabled={busy || doc.status === "rejected"} data-testid="compliance-reject">
              <X className="size-4" /> {t("compliance.detail.reject")}
            </Button>
          </div>
        </div>}
      {canModerate && rejecting && <div className="space-y-2 rounded-md border border-destructive/40 p-3">
          <label className="text-sm font-medium" htmlFor={`compliance-reject-${doc.id}`}>
            {t("compliance.detail.rejectLabel")}
          </label>
          <textarea id={`compliance-reject-${doc.id}`} value={reason} onChange={e_4 => setReason(e_4.target.value)} placeholder={t("compliance.detail.rejectPlaceholder")} className="min-h-20 w-full rounded-md border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-ring" data-testid="compliance-reject-reason" />
          {published > 0 && <p className="text-xs text-destructive">
              {t("compliance.review.rejectWarning", {
          count: published
        })}
            </p>}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="destructive" onClick={reject} disabled={busy} data-testid="compliance-reject-confirm">
              {t("compliance.detail.rejectConfirm")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRejecting(false)} disabled={busy}>
              {t("confirm.cancel")}
            </Button>
          </div>
        </div>}
      {error && <p className="text-destructive">{error}</p>}
    </div>;
}

/** Диалог проверки документа - из очереди. */
export function ComplianceReviewDialog(t0) {
  const $ = _c(24);
  const {
    doc,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = doc !== null;
  let t2;
  if ($[0] !== onClose) {
    t2 = next => !next && onClose();
    $[0] = onClose;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== doc || $[3] !== t) {
    t3 = doc ? t("compliance.review.title", {
      number: doc.number
    }) : "";
    $[2] = doc;
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <DialogTitle>{t3}</DialogTitle>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("compliance.review.hint");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <DialogDescription>{t5}</DialogDescription>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <DialogHeader>{t4}{t6}</DialogHeader>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== doc || $[15] !== onClose) {
    t8 = doc && <ComplianceReviewPanel doc={doc} onDone={onClose} />;
    $[14] = doc;
    $[15] = onClose;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-xl" data-testid="compliance-review-dialog">{t7}{t8}</DialogContent>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t9) {
    t10 = <Dialog open={t1} onOpenChange={t2}>{t9}</Dialog>;
    $[20] = t1;
    $[21] = t2;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}